import React, {useState} from "react";
import {useCollapsibleRows} from "@vigo/hooks";
import {
    Card,
    Table,
    Button,
    Tooltip,
    Row,
    message,
    Modal,
    Input,
    Form,
    Select,
    Popconfirm
} from "antd";
import {FileAddOutlined, DownloadOutlined} from "@ant-design/icons";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackofficePendingInvoicePaymentsFilters} from "./BackofficePendingInvoicePaymentsFilters";
import {CustomButton} from "../../../_defaultComponents/CustomButton/CustomButton";
import {b64DecodeToUrl, isConstantExist} from "../../../../utils/helpers";
import {codPaymentTypes} from "../../../../constants/constants";
import {useTranslation} from "react-i18next";
import {ExpandableStyledTable} from "components/_defaultComponents/ExpandableStyledTable/ExpandableStyledTable.styles";
const PAGE_SIZE = 10;

const InvoiceType = Object.freeze({
    EFATURA: "EFATURA",
    EARSIV: "EARSIV"
});

export const BackofficePendingInvoicePaymentsTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const expandableState = useCollapsibleRows({isTabOpened, page});
    const [recall, setRecall] = useState(false);
    const [form] = Form.useForm();
    const [selectedRow, setSelectedRow] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loadingExcelData, setLoadingExcelData] = useState(false);

    const [loading, codPaymentReferenceCodesResult] = useFetch({
        fetchFn: stores.codPaymentStore.getPendingInvoicePayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const handleAddInvoice = async (referenceCode = selectedRow?.referenceCode) => {
        try {
            const values = await form.validateFields();
            const result = await stores.codPaymentStore.assignInvoiceToPayment({
                referenceCode: referenceCode,
                documentId: values.documentId,
                invoiceType: values.invoiceType
            });
            if (!result) {
                throw new Error("E-Fatura eklenirken bir hata oluştu");
            } else {
                message.success("E-Fatura başarıyla eklendi");
                form.resetFields();
                setRecall(!recall);
            }
        } catch (error) {
            console.log(error);
            if (error?.errorFields) {
                message.error("Lütfen tüm alanları doldurunuz");
            } else {
                message.error("E-Fatura eklenirken bir hata oluştu");
            }
        }
    };

    const showModal = record => {
        form.resetFields();
        setSelectedRow(record);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setSelectedRow(null);
        setIsModalVisible(false);
    };

    const expandedRowRender = record => {
        const columns = [
            {
                title: "Ödeme Id",
                dataIndex: "_id",
                key: "transactionId",
                width: 100,
                ellipsis: {
                    showTitle: false
                },
                render: text => <Tooltip title={text}>{text}</Tooltip>,
                onCell: () => ({
                    "data-label": "Ödeme Id"
                })
            },
            {
                title: "Hesaba Yatırılacak Tutar",
                dataIndex: ["payment", "payableAmount"],
                key: "amount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Hesaba Yatırılacak Tutar"
                })
            },
            {
                title: "Fatura Tutarı",
                dataIndex: ["payment", "receivedAmount"],
                key: "invoiceAmount",
                width: 100,
                render: text => text && `${text} TL`,
                onCell: () => ({
                    "data-label": "Fatura Tutarı"
                })
            },
            {
                title: "İşlem Tarihi",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 150,
                render: text => new Date(text).toLocaleString("tr-TR"),
                onCell: () => ({
                    "data-label": "İşlem Tarihi"
                })
            },
            {
                title: "Mutabakat Tarihi",
                dataIndex: ["invoice", "reconciliationDate"],
                key: "reconciliationDate",
                width: 150,
                render: text => text && new Date(text).toLocaleDateString("tr-TR"),
                onCell: () => ({
                    "data-label": "Mutabakat Tarihi"
                })
            },

            {
                title: "Ödeme yöntemi",
                dataIndex: ["payment", "paymentType"],
                key: "paymentType",
                width: 120,
                render: paymentType =>
                    `${
                        isConstantExist(codPaymentTypes, paymentType)
                            ? t(`paymentTypes.${paymentType}`, {
                                  ns: "codPayment"
                              })
                            : paymentType
                    }`,
                onCell: () => ({
                    "data-label": "Ödeme Yöntemi"
                })
            },
            {
                title: "Sipariş No",
                dataIndex: ["delivery", "vigoOrderNumber"],
                key: "vigoOrderNumber",
                width: 100,
                render: (text, row) => (
                    <Link
                        data-testid="vigoOrderNumber"
                        target="_blank"
                        to={{
                            pathname: "/orders/" + row?.deliveryInfo?._id
                        }}
                    >
                        {text}
                    </Link>
                ),
                onCell: () => ({
                    "data-label": "Sipariş No"
                })
            },
            {
                title: "Kurye",
                dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
                key: "courier",
                width: 150,
                render: (text, row) => (
                    <Link
                        data-testid="courierName"
                        target="_blank"
                        to={{
                            pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                        }}
                    >
                        {text}
                    </Link>
                ),
                onCell: () => ({
                    "data-label": "Kurye"
                })
            }
        ];

        return (
            <ExpandableStyledTable
                columns={columns}
                dataSource={record.codPayments}
                pagination={false}
                rowKey="_id"
                size="small"
            />
        );
    };

    const columns = [
        {
            title: "Referans Fatura No",
            dataIndex: "referenceCode",
            key: "referenceCode",
            onCell: () => ({
                "data-label": "Referans Fatura No"
            })
        },
        // Referans kodu tek bir alt firmaya ait olduğu için ilk ödemenin firma bilgisi kullanılıyor
        {
            title: "Firma",
            dataIndex: ["codPayments", 0, "deliveryInfo", "order", "pickupInfoCompanyName"],
            key: "company",
            render: (text, record) => (
                <Link
                    to={`/companies/${record?.codPayments?.[0]?.deliveryInfo?.order?.pickupInfoCompanyId}`}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Firma"
            })
        },
        {
            title: "Alt Firma",
            dataIndex: ["codPayments", 0, "deliveryInfo", "order", "pickupInfoName"],
            key: "subCompany",
            render: (text, record) => (
                <Link
                    to={`/companies/sub-company/${record?.codPayments?.[0]?.deliveryInfo?.order?.pickupInfoId}`}
                >
                    {text}
                </Link>
            ),
            onCell: () => ({
                "data-label": "Alt Firma"
            })
        },
        {
            title: "Toplam Fatura Tutarı",
            dataIndex: "receivedAmount",
            key: "receivedAmount",
            render: text => `${text} TL`,
            onCell: () => ({
                "data-label": "Toplam Fatura Tutarı"
            })
        },
        {
            title: "Toplam İşlem Adedi",
            dataIndex: "codPayments",
            key: "codPayments",
            render: codPayments => codPayments?.length || 0,
            onCell: () => ({
                "data-label": "Toplam İşlem Adedi"
            })
        },
        {
            title: "Toplam Hesaba Yatırılacak Tutar",
            dataIndex: "payableAmount",
            key: "payableAmount",
            render: text => `${text} TL`,
            onCell: () => ({
                "data-label": "Toplam Hesaba Yatırılacak Tutar"
            })
        },
        {
            title: "İşlemler",
            key: "actions",
            render: (_, record) => (
                <Button size="small" icon={<FileAddOutlined />} onClick={() => showModal(record)}>
                    E-Fatura Ekle
                </Button>
            ),
            onCell: () => ({
                "data-label": "İşlemler"
            })
        }
    ];

    const getExcelTemplateData = async () => {
        setLoadingExcelData(true);
        try {
            const response = await stores.codPaymentStore.excelExportPendingInvoicePayments({
                ...filters
            });
            const base64Data = response?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "fatura-bekleyenler.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.log("error", error);
        } finally {
            setLoadingExcelData(false);
        }
    };

    return (
        <>
            <Card>
                <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                    <BackofficePendingInvoicePaymentsFilters
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Row>

                {stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                    <Row justify="end" style={{marginBottom: 16}}>
                        <div style={{marginTop: "15px"}}>
                            <CustomButton
                                type="primary"
                                data-testid="exportWithExcelButton"
                                onClick={async () => {
                                    await getExcelTemplateData();
                                }}
                                loading={loadingExcelData}
                                icon={<DownloadOutlined />}
                            >
                                Excel Çıktısı İndir
                            </CustomButton>
                        </div>
                    </Row>
                ) : (
                    <></>
                )}
                <ExpandableStyledTable
                    loading={loading}
                    columns={columns}
                    size="small"
                    dataSource={codPaymentReferenceCodesResult?.rows || []}
                    rowKey="_id"
                    expandable={{
                        ...expandableState,
                        expandedRowRender
                    }}
                    pagination={{
                        total: codPaymentReferenceCodesResult?.dataCount || 0,
                        current: page,
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        pageSize: PAGE_SIZE,
                        onChange: page => {
                            setPage(page);
                        }
                    }}
                />
            </Card>
            <Modal
                title="E-Fatura Ekle"
                visible={isModalVisible}
                onOk={() => handleAddInvoice()}
                onCancel={handleCancel}
                okText="Ekle"
                cancelText="İptal"
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        referenceCode: selectedRow?.referenceCode
                    }}
                >
                    <Form.Item name="referenceCode" label="Referans Kodu">
                        <Input disabled value={selectedRow?.referenceCode} />
                    </Form.Item>
                    <Form.Item
                        name="documentId"
                        label="Fatura Numarası"
                        rules={[{required: true, message: "Lütfen fatura numarasını giriniz"}]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="invoiceType"
                        label="Fatura Tipi"
                        rules={[{required: true, message: "Lütfen fatura tipini seçiniz"}]}
                    >
                        <Select>
                            <Select.Option value={InvoiceType.EFATURA}>E-Fatura</Select.Option>
                            <Select.Option value={InvoiceType.EARSIV}>E-Arşiv</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
